/**
 * AssignedRecruitersPanel — Batch 2 (Feb 2026)
 *
 * Renders the recruiters currently assigned to a job. Admin & hiring_manager
 * users get the full assign/unassign UI. Recruiters see a read-only chip list.
 *
 * Props:
 *  - jobId (string) — required
 *  - assignedRecruiters (array of { id, name, email }) — current chips
 *  - onChange (function) — called with the fresh job doc after assign/unassign
 *
 * The component is intentionally self-contained: it fetches
 * /api/users/recruiters lazily when the add-picker opens.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Plus, Users, LockKey } from "@phosphor-icons/react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AssignedRecruitersPanel({ jobId, assignedRecruiters = [], onChange }) {
    const { user } = useAuth();
    const canManage = user && (user.role === "admin" || user.role === "hiring_manager");

    const [picker, setPicker] = useState(false);
    const [allRecruiters, setAllRecruiters] = useState(null);
    const [busyIds, setBusyIds] = useState({});
    // { id, name } of recruiter pending confirm-unassign, or null.
    const [pendingUnassign, setPendingUnassign] = useState(null);

    useEffect(() => {
        if (!picker || allRecruiters !== null || !canManage) return;
        api.get("/users/recruiters")
            .then((r) => setAllRecruiters(r.data || []))
            .catch((e) => {
                logError("Failed to load recruiters:", e);
                setAllRecruiters([]);
            });
    }, [picker, allRecruiters, canManage]);

    const assignedIds = useMemo(
        () => new Set((assignedRecruiters || []).map((r) => r.id)),
        [assignedRecruiters],
    );
    const availableToAdd = useMemo(
        () => (allRecruiters || []).filter((r) => !assignedIds.has(r.id)),
        [allRecruiters, assignedIds],
    );

    const setBusy = (id, val) => setBusyIds((prev) => ({ ...prev, [id]: val }));

    const doAssign = async (recruiterId) => {
        setBusy(recruiterId, true);
        try {
            const res = await api.post(`/jobs/${jobId}/assign`, { recruiter_ids: [recruiterId] });
            toast.success("Recruiter assigned.");
            onChange?.(res.data);
            setPicker(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't assign recruiter.");
        } finally {
            setBusy(recruiterId, false);
        }
    };

    const doUnassign = async (recruiterId) => {
        setBusy(recruiterId, true);
        try {
            const res = await api.post(`/jobs/${jobId}/unassign`, { recruiter_ids: [recruiterId] });
            toast.success("Recruiter unassigned. Their history is preserved.");
            onChange?.(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't unassign recruiter.");
        } finally {
            setBusy(recruiterId, false);
            setPendingUnassign(null);
        }
    };

    return (
        <div
            data-testid="assigned-recruiters-panel"
            className="mb-8 bg-white border border-[#E5E5E5] p-4 sm:p-5"
        >
            <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] inline-flex items-center gap-1.5">
                    <Users size={11} weight="bold" /> Assigned Recruiters
                    {!canManage && (
                        <span className="text-[9px] text-[#A3A3A3] normal-case tracking-normal font-normal inline-flex items-center gap-0.5 ml-1">
                            <LockKey size={9} /> read-only
                        </span>
                    )}
                </p>
                {canManage && !picker && (
                    <button
                        data-testid="assign-recruiter-open-picker"
                        onClick={() => setPicker(true)}
                        className="text-[11px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1"
                    >
                        <Plus size={12} weight="bold" /> Assign
                    </button>
                )}
            </div>

            {(assignedRecruiters || []).length === 0 && !picker && (
                <p
                    data-testid="assigned-recruiters-empty"
                    className="text-sm text-[#A3A3A3]"
                >
                    {canManage
                        ? "No recruiters assigned yet. Click Assign to allocate this requirement."
                        : "You have historical access to this requirement — no active assignees."}
                </p>
            )}

            {(assignedRecruiters || []).length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="assigned-recruiter-chips">
                    {assignedRecruiters.map((r) => (
                        <span
                            key={r.id}
                            data-testid={`assigned-recruiter-chip-${r.id}`}
                            className="text-[11px] font-medium px-2.5 py-1 bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE] inline-flex items-center gap-1.5"
                            title={r.email}
                        >
                            {r.name}
                            {canManage && (
                                <button
                                    onClick={() => setPendingUnassign({ id: r.id, name: r.name })}
                                    disabled={busyIds[r.id]}
                                    data-testid={`unassign-recruiter-${r.id}`}
                                    className="text-[#1E40AF] hover:text-[#DC2626] disabled:opacity-50"
                                    aria-label={`Unassign ${r.name}`}
                                >
                                    <X size={11} weight="bold" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}

            {picker && canManage && (
                <div className="mt-4 border-t border-[#E5E5E5] pt-3" data-testid="assign-recruiter-picker">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                            Pick a recruiter
                        </p>
                        <button
                            onClick={() => setPicker(false)}
                            className="text-[10px] uppercase text-[#A3A3A3] hover:text-[#525252]"
                            data-testid="assign-recruiter-close-picker"
                        >
                            Cancel
                        </button>
                    </div>
                    {allRecruiters === null && (
                        <p className="text-sm text-[#A3A3A3]">Loading recruiters…</p>
                    )}
                    {allRecruiters !== null && availableToAdd.length === 0 && (
                        <p className="text-sm text-[#A3A3A3]">
                            {allRecruiters.length === 0
                                ? "No recruiters in this workspace yet. Invite one from the Team page."
                                : "All available recruiters are already assigned."}
                        </p>
                    )}
                    {availableToAdd.length > 0 && (
                        <div className="grid gap-1.5 sm:grid-cols-2 max-h-56 overflow-y-auto">
                            {availableToAdd.map((r) => (
                                <button
                                    key={r.id}
                                    onClick={() => doAssign(r.id)}
                                    disabled={busyIds[r.id]}
                                    data-testid={`assign-recruiter-pick-${r.id}`}
                                    className="text-left text-sm px-3 py-2 bg-[#F9FAFB] hover:bg-[#EFF6FF] border border-transparent hover:border-[#BFDBFE] disabled:opacity-50 transition"
                                >
                                    <div className="font-medium text-[#0A0A0A]">{r.name}</div>
                                    <div className="text-[10px] text-[#A3A3A3]">{r.email}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <AlertDialog
                open={pendingUnassign !== null}
                onOpenChange={(o) => !o && setPendingUnassign(null)}
            >
                <AlertDialogContent data-testid="unassign-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Remove {pendingUnassign?.name} from this requirement?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            They&apos;ll keep historical read access to the candidates they
                            worked with — but this requirement won&apos;t appear in their
                            active list going forward.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-unassign-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-unassign-yes"
                            onClick={() => pendingUnassign && doUnassign(pendingUnassign.id)}
                            className="bg-[#DC2626] hover:bg-[#B91C1C]"
                        >
                            Unassign
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
